import { Sidebar } from "@/components/Sidebar";
import WelcomeMessage from "@/components/WelcomeMessage";
import { JSX, useEffect, useState } from "react";
import { SignatureProvider } from "@/context/SignatureContext";
import Header from "@/components/Header";
import { Instructions } from "@/components/Instructions";

export interface User {
  displayName: string;
  givenName: string;
  surname: string;
  mail: string;
  photoUrl: string | null;
  jobTitle: string | null;
  mobilePhone: string | null;
  businessPhones: string[];
}

// key under which we’ll store the flag
const SEEN_INSTRUCTIONS_KEY = "hasSeenInstructions";

const HomePage = (): JSX.Element => {
  // initialize state based on localStorage
  const [isInstructionsOpen, setIsInstructionsOpen] = useState(() => {
    // if the key is "true", we’ve already seen it => don’t show again
    return localStorage.getItem(SEEN_INSTRUCTIONS_KEY) !== "true";
  });

  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function whoami() {
      const res = await fetch("/auth/me");
      if (res.status === 401) {
        // not logged in → start auth
        window.location.href = "/auth/login";
        return;
      }
      const u: User = await res.json();
      setUser(u);
      setLoading(false);
    }
    whoami();
  }, []);

  const handleSignOut = async () => {
    await fetch("/auth/logout", { method: "POST" }).then(() => {});
    setUser(undefined);
  };

  useEffect(() => {
    if (!isInstructionsOpen) {
      localStorage.setItem(SEEN_INSTRUCTIONS_KEY, "true");
    }
  }, [isInstructionsOpen]);

  const handleSeeInstructions = () => {
    // Logic to open instructions modal here
    setIsInstructionsOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center bg-white shadow-md p-4 rounded-lg text-textDefault">
          <img src="/images/logo.svg" alt="Logo" className="w-1/2 max-w-[250px]" />
          Loading...
        </div>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center gap-2 bg-white shadow-md p-4 rounded-lg text-textDefault">
          <img src="/images/logo.svg" alt="Logo" className="w-1/2 max-w-[250px]" />
          <div className="text-lg">
            Thanks for using our tool, please login again to use the signature tool
          </div>
        </div>
      </div>
    );
  }

  return (
    <SignatureProvider>
      <Instructions isOpen={isInstructionsOpen} setIsOpen={setIsInstructionsOpen} />
      <div className="bg-ccNeutral800">
        <Header
          userImage={user?.photoUrl || "/images/profile-placeholder.svg"}
          onSignOut={handleSignOut}
          onSeeInstructions={handleSeeInstructions}
        />
        <div className="flex md:flex-row flex-col justify-center gap-4 p-4 pb-0">
          <Sidebar user={user} />
          <div className="grow">
            <WelcomeMessage user={user} />
          </div>
        </div>
      </div>
    </SignatureProvider>
  );
};

export default HomePage;
